import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Pencil,
  Plane,
  Train,
  Car,
  MapPin,
  Calendar,
  Hotel,
  Utensils,
  Bus,
  Coins,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Calculator,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { API } from '../utils/api';

const TRAVEL_TYPES = [
  { value: "flight", label: "Flight", icon: Plane },
  { value: "train", label: "Train", icon: Train },
  { value: "car", label: "Car", icon: Car },
];

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function HolidayPlanning({ user }) {
  const [plansByYear, setPlansByYear] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actualsDialogOpen, setActualsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});
  const [selectedPlanForActuals, setSelectedPlanForActuals] = useState(null);
  
  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState({
    name: "",
    year: currentYear,
    destination: "",
    duration_days: 7,
    start_date: "",
    budget: {
      travel_type: "flight",
      travel_cost: 0,
      hotel_per_day: 0,
      food_per_day: 0,
      transport_per_day: 0,
      misc_per_day: 0,
    },
    notes: "",
  });

  const [actualsData, setActualsData] = useState({
    travel_cost: 0,
    hotel_total: 0,
    food_total: 0,
    transport_total: 0,
    misc_total: 0,
  });

  const currency = user?.default_currency || "EUR";

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/holiday-plans/by-year`);
      setPlansByYear(response.data);
      
      // Expand current year and next year by default
      const years = Object.keys(response.data).map(Number);
      const defaultExpanded = {};
      years.forEach(year => {
        if (year >= currentYear) {
          defaultExpanded[year] = true;
        }
      });
      setExpandedYears(defaultExpanded);
    } catch (error) {
      console.error("Error fetching holiday plans:", error);
      toast.error("Failed to load holiday plans");
    } finally {
      setLoading(false);
    }
  };

  const calculateBudgetTotal = (budget, days) => {
    const travel = budget?.travel_cost || 0;
    const hotel = (budget?.hotel_per_day || 0) * days;
    const food = (budget?.food_per_day || 0) * days;
    const transport = (budget?.transport_per_day || 0) * days;
    const misc = (budget?.misc_per_day || 0) * days;
    return travel + hotel + food + transport + misc;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.year) {
      toast.error("Please fill in plan name and year");
      return;
    }

    try {
      if (editingPlan) {
        await axios.put(`${API}/holiday-plans/${editingPlan.plan_id}`, formData);
        toast.success("Holiday plan updated");
      } else {
        await axios.post(`${API}/holiday-plans`, formData);
        toast.success("Holiday plan created");
      }
      setDialogOpen(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error("Error saving holiday plan:", error);
      toast.error("Failed to save holiday plan");
    }
  };

  const handleDelete = async (planId) => {
    try {
      await axios.delete(`${API}/holiday-plans/${planId}`);
      toast.success("Holiday plan deleted");
      fetchPlans();
    } catch (error) {
      console.error("Error deleting holiday plan:", error);
      toast.error("Failed to delete holiday plan");
    }
  };

  const handleSaveActuals = async () => {
    if (!selectedPlanForActuals) return;
    
    try {
      await axios.put(`${API}/holiday-plans/${selectedPlanForActuals.plan_id}/actuals`, actualsData);
      toast.success("Actuals updated");
      setActualsDialogOpen(false);
      setSelectedPlanForActuals(null);
      fetchPlans();
    } catch (error) {
      console.error("Error saving actuals:", error);
      toast.error("Failed to save actuals");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      year: currentYear,
      destination: "",
      duration_days: 7,
      start_date: "",
      budget: {
        travel_type: "flight",
        travel_cost: 0,
        hotel_per_day: 0,
        food_per_day: 0,
        transport_per_day: 0,
        misc_per_day: 0,
      },
      notes: "",
    });
    setEditingPlan(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      year: plan.year,
      destination: plan.destination || "",
      duration_days: plan.duration_days || 7,
      start_date: plan.start_date || "",
      budget: plan.budget || {
        travel_type: "flight",
        travel_cost: 0,
        hotel_per_day: 0,
        food_per_day: 0,
        transport_per_day: 0,
        misc_per_day: 0,
      },
      notes: plan.notes || "",
    });
    setDialogOpen(true);
  };

  const openActualsDialog = (plan) => {
    setSelectedPlanForActuals(plan);
    setActualsData(plan.actuals || {
      travel_cost: 0,
      hotel_total: 0,
      food_total: 0,
      transport_total: 0,
      misc_total: 0,
    });
    setActualsDialogOpen(true);
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const getTravelIcon = (type) => {
    const found = TRAVEL_TYPES.find(t => t.value === type);
    return found ? found.icon : Plane;
  };

  const budgetTotal = calculateBudgetTotal(formData.budget, formData.duration_days);
  const actualsTotal = actualsData.travel_cost + actualsData.hotel_total + 
                       actualsData.food_total + actualsData.transport_total + actualsData.misc_total;

  // Calculate yearly totals
  const yearlyTotals = Object.entries(plansByYear).reduce((acc, [year, plans]) => {
    const budgetTotal = plans.reduce((sum, plan) => sum + (plan.budget_totals?.grand_total || 0), 0);
    const actualsTotal = plans.reduce((sum, plan) => sum + (plan.actuals_total || 0), 0);
    acc[year] = { budgetTotal, actualsTotal, count: plans.length };
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout user={user} pageTitle="Holiday Planning">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Holiday Planning">
      <div data-testid="holiday-planning-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Holiday Planning</h1>
            <p className="text-gray-400">
              Plan and track your vacation budgets year by year
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
            data-testid="create-holiday-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Holiday Plan
          </Button>
        </div>

        {/* Summary Card */}
        {Object.keys(plansByYear).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Holidays</p>
                    <p className="text-2xl font-bold text-blue-400">
                      {Object.values(plansByYear).flat().length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Budget ({currentYear})</p>
                    <p className="text-2xl font-bold text-amber-400 number-display">
                      {formatCurrency(yearlyTotals[currentYear]?.budgetTotal || 0, currency)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center">
                    <Coins className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Actual Spent ({currentYear})</p>
                    <p className="text-2xl font-bold text-green-400 number-display">
                      {formatCurrency(yearlyTotals[currentYear]?.actualsTotal || 0, currency)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Plans by Year */}
        {Object.keys(plansByYear).length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-6">
                <Plane className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Holiday Plans Yet</h3>
              <p className="text-gray-400 mb-6">
                Start planning your adventures by creating your first holiday plan.
              </p>
              <Button
                onClick={openCreateDialog}
                className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Plan
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(plansByYear)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([year, plans]) => (
                <Card key={year} className="bg-card border-border overflow-hidden">
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/50 transition-colors"
                    onClick={() => toggleYear(year)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">{year}</h2>
                        <p className="text-sm text-gray-500">
                          {plans.length} {plans.length === 1 ? 'holiday' : 'holidays'} planned
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Budget</p>
                        <p className="font-bold text-amber-400 number-display">
                          {formatCurrency(yearlyTotals[year]?.budgetTotal || 0, currency)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Actual</p>
                        <p className="font-bold text-green-400 number-display">
                          {formatCurrency(yearlyTotals[year]?.actualsTotal || 0, currency)}
                        </p>
                      </div>
                      {expandedYears[year] ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                  
                  <AnimatePresence>
                    {expandedYears[year] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="border-t border-border p-4 space-y-4">
                          {plans.map((plan) => {
                            const TravelIcon = getTravelIcon(plan.budget?.travel_type);
                            const hasActuals = plan.actuals_total > 0;
                            const variance = hasActuals ? (plan.budget_totals?.grand_total || 0) - plan.actuals_total : null;
                            
                            return (
                              <motion.div
                                key={plan.plan_id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 bg-secondary rounded-xl"
                              >
                                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                                  {/* Plan Info */}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                        <TravelIcon className="w-4 h-4 text-blue-400" />
                                      </div>
                                      <div>
                                        <h3 className="font-bold text-white">{plan.name}</h3>
                                        {plan.destination && (
                                          <p className="text-sm text-gray-400 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {plan.destination}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-4 h-4" /> {plan.duration_days} days
                                      </span>
                                      {plan.start_date && (
                                        <span>{new Date(plan.start_date).toLocaleDateString()}</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Budget Breakdown */}
                                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
                                    <div className="p-2 bg-background rounded-lg">
                                      <Plane className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                                      <p className="text-xs text-gray-500">Travel</p>
                                      <p className="text-sm font-bold text-white number-display">
                                        {formatCurrency(plan.budget?.travel_cost || 0, currency)}
                                      </p>
                                    </div>
                                    <div className="p-2 bg-background rounded-lg">
                                      <Hotel className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                                      <p className="text-xs text-gray-500">Hotel</p>
                                      <p className="text-sm font-bold text-white number-display">
                                        {formatCurrency(plan.budget_totals?.hotel_total || 0, currency)}
                                      </p>
                                    </div>
                                    <div className="p-2 bg-background rounded-lg">
                                      <Utensils className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                                      <p className="text-xs text-gray-500">Food</p>
                                      <p className="text-sm font-bold text-white number-display">
                                        {formatCurrency(plan.budget_totals?.food_total || 0, currency)}
                                      </p>
                                    </div>
                                    <div className="p-2 bg-background rounded-lg">
                                      <Bus className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                                      <p className="text-xs text-gray-500">Transport</p>
                                      <p className="text-sm font-bold text-white number-display">
                                        {formatCurrency(plan.budget_totals?.transport_total || 0, currency)}
                                      </p>
                                    </div>
                                    <div className="p-2 bg-background rounded-lg">
                                      <Coins className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
                                      <p className="text-xs text-gray-500">Misc</p>
                                      <p className="text-sm font-bold text-white number-display">
                                        {formatCurrency(plan.budget_totals?.misc_total || 0, currency)}
                                      </p>
                                    </div>
                                    <div className="p-2 bg-green-500/10 rounded-lg border border-green-500/30">
                                      <Calculator className="w-4 h-4 text-green-400 mx-auto mb-1" />
                                      <p className="text-xs text-gray-500">Total</p>
                                      <p className="text-sm font-bold text-green-400 number-display">
                                        {formatCurrency(plan.budget_totals?.grand_total || 0, currency)}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {/* Actions */}
                                  <div className="flex lg:flex-col items-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openActualsDialog(plan)}
                                      className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                                    >
                                      <TrendingUp className="w-4 h-4 mr-1" />
                                      Actuals
                                    </Button>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-500 hover:text-blue-400"
                                        onClick={() => openEditDialog(plan)}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-500 hover:text-red-400"
                                        onClick={() => handleDelete(plan.plan_id)}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Actuals vs Budget */}
                                {hasActuals && (
                                  <div className="mt-4 pt-4 border-t border-border/50">
                                    <div className="flex flex-wrap items-center justify-between gap-4">
                                      <div className="flex items-center gap-4">
                                        <span className="text-sm text-gray-400">Actuals:</span>
                                        <span className="font-bold text-white number-display">
                                          {formatCurrency(plan.actuals_total, currency)}
                                        </span>
                                      </div>
                                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                                        variance >= 0 
                                          ? 'bg-green-500/10 text-green-400' 
                                          : 'bg-red-500/10 text-red-400'
                                      }`}>
                                        {variance >= 0 ? (
                                          <TrendingDown className="w-4 h-4" />
                                        ) : (
                                          <TrendingUp className="w-4 h-4" />
                                        )}
                                        <span className="font-bold number-display">
                                          {variance >= 0 ? 'Under budget: ' : 'Over budget: '}
                                          {formatCurrency(Math.abs(variance), currency)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border text-white">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingPlan ? "Edit Holiday Plan" : "Create Holiday Plan"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300">Holiday Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Summer in Italy"
                    required
                    className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Year</Label>
                  <Select
                    value={formData.year.toString()}
                    onValueChange={(value) => setFormData({ ...formData, year: parseInt(value) })}
                  >
                    <SelectTrigger className="mt-1 bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {[...Array(5)].map((_, i) => {
                        const year = currentYear + i;
                        return (
                          <SelectItem key={year} value={year.toString()} className="text-gray-300 focus:text-white focus:bg-secondary">
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Duration (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 1 })}
                    className="mt-1 bg-secondary border-border text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Destination</Label>
                  <Input
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    placeholder="e.g., Rome, Italy"
                    className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Start Date (optional)</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1 bg-secondary border-border text-white"
                  />
                </div>
              </div>

              {/* Budget Section */}
              <div className="border-t border-border pt-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-green-400" />
                  Budget Estimation
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Travel Type</Label>
                    <Select
                      value={formData.budget.travel_type}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        budget: { ...formData.budget, travel_type: value }
                      })}
                    >
                      <SelectTrigger className="mt-1 bg-secondary border-border text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {TRAVEL_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value} className="text-gray-300 focus:text-white focus:bg-secondary">
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Travel Cost (total) ({currency})</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.budget.travel_cost}
                      onChange={(e) => setFormData({
                        ...formData,
                        budget: { ...formData.budget, travel_cost: parseFloat(e.target.value) || 0 }
                      })}
                      className="mt-1 bg-secondary border-border text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Hotel per day ({currency})</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.budget.hotel_per_day}
                      onChange={(e) => setFormData({
                        ...formData,
                        budget: { ...formData.budget, hotel_per_day: parseFloat(e.target.value) || 0 }
                      })}
                      className="mt-1 bg-secondary border-border text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Food per day ({currency})</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.budget.food_per_day}
                      onChange={(e) => setFormData({
                        ...formData,
                        budget: { ...formData.budget, food_per_day: parseFloat(e.target.value) || 0 }
                      })}
                      className="mt-1 bg-secondary border-border text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Local Transport per day ({currency})</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.budget.transport_per_day}
                      onChange={(e) => setFormData({
                        ...formData,
                        budget: { ...formData.budget, transport_per_day: parseFloat(e.target.value) || 0 }
                      })}
                      className="mt-1 bg-secondary border-border text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Misc per day ({currency})</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.budget.misc_per_day}
                      onChange={(e) => setFormData({
                        ...formData,
                        budget: { ...formData.budget, misc_per_day: parseFloat(e.target.value) || 0 }
                      })}
                      className="mt-1 bg-secondary border-border text-white"
                    />
                  </div>
                </div>

                {/* Budget Summary */}
                <div className="mt-4 p-4 bg-green-500/10 rounded-xl border border-green-500/30">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Estimated Total ({formData.duration_days} days)</span>
                    <span className="text-2xl font-bold text-green-400 number-display">
                      {formatCurrency(budgetTotal, currency)}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-gray-400">
                    Travel: {formatCurrency(formData.budget.travel_cost, currency)} + 
                    Hotel: {formatCurrency(formData.budget.hotel_per_day * formData.duration_days, currency)} + 
                    Food: {formatCurrency(formData.budget.food_per_day * formData.duration_days, currency)} + 
                    Transport: {formatCurrency(formData.budget.transport_per_day * formData.duration_days, currency)} + 
                    Misc: {formatCurrency(formData.budget.misc_per_day * formData.duration_days, currency)}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-gray-300">Notes (optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
                >
                  {editingPlan ? "Update Plan" : "Create Plan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Actuals Dialog */}
        <Dialog open={actualsDialogOpen} onOpenChange={setActualsDialogOpen}>
          <DialogContent className="sm:max-w-lg bg-card border-border text-white">
            <DialogHeader>
              <DialogTitle className="text-white">
                Enter Actuals - {selectedPlanForActuals?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Enter your actual spending for this holiday to compare with your budget.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Travel Cost ({currency})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={actualsData.travel_cost}
                    onChange={(e) => setActualsData({ ...actualsData, travel_cost: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-secondary border-border text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Hotel Total ({currency})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={actualsData.hotel_total}
                    onChange={(e) => setActualsData({ ...actualsData, hotel_total: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-secondary border-border text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Food Total ({currency})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={actualsData.food_total}
                    onChange={(e) => setActualsData({ ...actualsData, food_total: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-secondary border-border text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Transport Total ({currency})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={actualsData.transport_total}
                    onChange={(e) => setActualsData({ ...actualsData, transport_total: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-secondary border-border text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Misc Total ({currency})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={actualsData.misc_total}
                    onChange={(e) => setActualsData({ ...actualsData, misc_total: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-secondary border-border text-white"
                  />
                </div>
              </div>

              {/* Actuals Summary */}
              <div className="p-4 bg-secondary rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-300">Actual Total</span>
                  <span className="text-xl font-bold text-white number-display">
                    {formatCurrency(actualsTotal, currency)}
                  </span>
                </div>
                {selectedPlanForActuals && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Budget</span>
                    <span className="text-gray-400 number-display">
                      {formatCurrency(selectedPlanForActuals.budget_totals?.grand_total || 0, currency)}
                    </span>
                  </div>
                )}
                {selectedPlanForActuals && (
                  <div className={`flex items-center justify-between mt-2 pt-2 border-t border-border ${
                    (selectedPlanForActuals.budget_totals?.grand_total || 0) - actualsTotal >= 0
                      ? 'text-green-400'
                      : 'text-red-400'
                  }`}>
                    <span>Variance</span>
                    <span className="font-bold number-display">
                      {formatCurrency((selectedPlanForActuals.budget_totals?.grand_total || 0) - actualsTotal, currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setActualsDialogOpen(false)}
                className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveActuals}
                className="bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
              >
                Save Actuals
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
